import { Injectable, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, filter, firstValueFrom, from, map, switchMap, take } from "rxjs";

import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { DeviceService } from "@services/device.service";
import { AppsFlyerTrackingService } from "@shared-providers/AppsFlyerTrackingService";
import { BiometricService } from "@shared-providers/BiometricService";
import { CookiesService } from "@shared-providers/CookiesService";
import {
  DecouplingUserServiceMSO,
  ErrorCodes,
  ErrorDecoupling,
} from "@shared-providers/DecouplingUserServiceMSO.service";
import { ErrorService } from "@shared-providers/ErrorService";
import { EventsService } from "@shared-providers/EventsService";
import { GTMData, GTMDataManagerService, TrackStatus } from "@shared-providers/GTMDataManager.service";
import { Coords, GeolocationService } from "@shared-providers/GeolocationService";
import { LogService } from "@shared-providers/LogService";
import { NativeService } from "@shared-providers/NativeService";
import { NewLoginAlertsService } from "@shared-providers/NewLoginAlerts.service";
import { NewUserService } from "@shared-providers/NewUserService";
import { SlotsBonusesService } from "@shared-providers/SlotsBonusesService";
import { TrackingService } from "@shared-providers/TrackingService";
import { UserMigrationService } from "@shared-providers/UserMigrationService";
import { UserService } from "@shared-providers/UserService";
import { UserServiceMSO } from "@shared-providers/UserServiceMSO";
import { NewBaseService } from "@shared-providers/newBase.service";
import { Utils } from "@shared-utils/Utils";

import { AnalyticsEvent, CustomDimensions } from "@constants";
import { CookieNames } from "@models/CookieNames";
import { AutologinStatus, DecouplingLoginResponse } from "@models/I_DecouplingLogin";
import { I_UserInfo } from "@models/I_UserInfo";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { EventTypes, TrackingEvent } from "@models/TrackingEvents";
import { DataUserMSOService, GraceTimeStatus } from "@mso-services/dataUserMSO.services";
import { SoliticsService } from "@providers/tracking/tracking/solitics/shared/solitics.service";
import { AnalyticsService } from "@services/analytics.service";
import { MSO_PATHS } from "@shared-constants/routes";
import { MessageService } from "@shared-providers/MessageService";
import * as Constants from "@shared-utils/constants";
import { NewLoginComponent, Steps } from "../pages/mso/new-login/new-login.component";

const USERNAME_STORAGE_KEY = "userNative";
const PASSWORD_STORAGE_KEY = "passNative";

declare var fbq;

enum LoginErrors {
  PassworldBlocked = 183,
  FailureSiebel = 7,
  LoginFailureCuentaCancelada = 709,
  LoginFailureCuentaSuspendida = 707,
  LoginFailureContratoAnulado = 708,
  LoginFailureSuspensionCautelar = 143,
  LoginFailureForbiddenActionLogin = 101,
  FailureTermsAndConditions = 198,
  FailurePoliticaDePrivacidad = 194,
  SelfExcludedInLotba = 25,
  LoginFailureGeolocationServiceNotAvailable = 195,
  LoginFailureGeolocationRequiredCoordinates = 196,
  LoginFailureGeolocation = 197,
  LoginFailureExprohibited = 711,
  LoginFailureRuamDown = 728,
}

@Injectable({
  providedIn: "root",
})
export class NewLoginService {
  public captchaUrl: BehaviorSubject<string> = new BehaviorSubject("");
  private globalVars!: GlobalVarsModel;
  private loginComponent: NewLoginComponent;
  private isMobile = false;
  private loginData: { userName: string; password: string; rememberMe: boolean };
  private coords: Coords = null;
  public isGeolocating = signal(false);
  public canOpenLogin = signal(true);
  private $loginEventDone = new BehaviorSubject(false);

  private newBaseService = inject(NewBaseService);
  private logService = inject(LogService);
  private decouplingService = inject(DecouplingUserServiceMSO);
  private trackingService = inject(TrackingService);
  private biometricService = inject(BiometricService);
  private utils = inject(Utils);
  private nativeService = inject(NativeService);
  private events = inject(EventsService);
  private cookiesService = inject(CookiesService);
  private userService = inject(UserService);
  private userServiceMSO = inject(UserServiceMSO);
  private userMigrationService = inject(UserMigrationService);
  private appsflyerTrackingService = inject(AppsFlyerTrackingService);
  private slotsBonusesService = inject(SlotsBonusesService);
  private geolocationService = inject(GeolocationService);
  private GTMService = inject(GTMDataManagerService);
  private router = inject(Router);
  private newLoginAlertsService = inject(NewLoginAlertsService);
  private errorService = inject(ErrorService);
  private deviceService = inject(DeviceService);
  private newUserService = inject(NewUserService);
  private utilsMsoService = inject(UtilsMsoService);
  private dataUserMsoService = inject(DataUserMSOService);
  private analyticsService = inject(AnalyticsService);
  private messageService = inject(MessageService);
  private soliticsService = inject(SoliticsService);

  constructor() {
    this.newBaseService.getVars.subscribe((data) => {
      this.globalVars = data;
    });

    this.isMobile = this.deviceService.isMobile();

    this.events.subscribe("user:login", () => {
      this.$loginEventDone.next(true);
    });
  }

  private get isIOSWebApp(): boolean {
    return Boolean(window.navigator["standalone"]);
  }

  get getUserName(): string {
    try {
      return localStorage.getItem(USERNAME_STORAGE_KEY) || "";
    } catch (_) {
      this.logService.error("localStorage error");
      return "";
    }
  }

  private get getPasswordNative(): string {
    try {
      return localStorage.getItem(PASSWORD_STORAGE_KEY) || "";
    } catch (_) {
      this.logService.error("localStorage error");
      return "";
    }
  }

  private set setUserName(userName: string) {
    try {
      localStorage.setItem(USERNAME_STORAGE_KEY, userName);
    } catch (_) {
      this.logService.error("localStorage error");
    }
  }

  private set setPasswordNative(password: string) {
    try {
      localStorage.setItem(PASSWORD_STORAGE_KEY, password);
    } catch (_) {
      this.logService.error("localStorage error");
    }
  }

  private removePassNative() {
    try {
      localStorage.removeItem(PASSWORD_STORAGE_KEY);
    } catch (_) {
      this.logService.error("localStorage error");
    }
  }

  set setLoginComponent(login: NewLoginComponent) {
    this.canOpenLogin.set(false);
    this.loginComponent = login;
  }

  removeLoginComponent() {
    this.loginComponent = undefined;
    this.canOpenLogin.set(true);
  }

  private finishLoginOK(
    loginResponse: DecouplingLoginResponse,
    autoLogin: boolean = false,
    isTouchId: boolean = false,
  ) {
    this.setAllUserData(loginResponse);

    this.getUserInfo();
    this.cookiesService.setCookie(CookieNames.CodereUser, loginResponse.playerUserName, 365 * 24 * 60 * 60 * 1000);

    this.setCustomization();

    this.migrationUser(loginResponse, autoLogin);

    this.setTrackersLoginOk(loginResponse, isTouchId);

    this.initGeolocation();
  }

  private finishLoginKO(
    errorResponse: ErrorDecoupling,
    userName: string,
    isTouchId: boolean = false,
    handlerErrorFN?: VoidFunction,
  ) {
    this.trackLoginKO(userName, isTouchId);

    const { code, additionalData, type } = errorResponse;
    this.coords = null;

    if (type === LoginErrors.LoginFailureExprohibited) {
      this.newLoginAlertsService
        .showAlertExProhibited(userName, Number(additionalData?.balance || 0))
        .then((accepted) => {
          if (accepted) {
            this.reLogin(isTouchId);
          } else {
            if (this.loginComponent) {
              this.loginComponent.closeLogin();
            }
            this.router.navigateByUrl(`${MSO_PATHS.BASE}/${MSO_PATHS.ContactPage}`);
          }
        })
        .catch(() => {
          if (this.loginComponent) {
            this.loginComponent.closeLogin();
          }
        });
      return;
    }

    if (type === LoginErrors.FailureTermsAndConditions) {
      this.newTermsAndConditions(userName, isTouchId);
      return;
    }

    if (type === LoginErrors.FailurePoliticaDePrivacidad) {
      this.newPrivacePoliticsAlert(userName, isTouchId);
      return;
    }

    if (code === "isTill") {
      if (this.loginComponent) {
        this.loginComponent.closeLogin();
      }
      this.newLoginAlertsService.showAlertTillPanama().then(() => {
        this.router.navigate([`/${MSO_PATHS.BASE}/${MSO_PATHS.ComunicationPage}`], {
          state: {
            dataTill: additionalData?.playerInTill,
            documentationVerification: additionalData?.documentationVerification,
          },
        });
      });
      return;
    }

    if (type === LoginErrors.LoginFailureForbiddenActionLogin) {
      this.newLoginAlertsService.showAlertUserBlock();
      return;
    }

    if (code === "302") {
      this.argentinaRedirectLicense(Number(additionalData?.redirect), String(additionalData?.jwt));
      return;
    }

    if (
      type === LoginErrors.LoginFailureGeolocation ||
      type === LoginErrors.LoginFailureGeolocationServiceNotAvailable ||
      type === LoginErrors.LoginFailureGeolocationRequiredCoordinates
    ) {
      this.geolocationError(type, handlerErrorFN);
      return;
    }

    if (type === LoginErrors.SelfExcludedInLotba) {
      this.utils.selfExclusionModal(2);
      return;
    }

    if (type === LoginErrors.PassworldBlocked) {
      this.newLoginAlertsService.showAlertChangePassword().then((accepted) => {
        if (accepted) {
          if (this.loginComponent) {
            this.loginComponent.gotoStep(Steps.GetPassword);
          }
        } else {
          if (isTouchId) {
            this.nativeService.reOpenLogin();
            this.removePassNative();
            this.biometricService.delete();
          }
        }
      });
      return;
    }

    if (type === LoginErrors.LoginFailureRuamDown) {
      this.newLoginAlertsService.showAlertRuamDown().then(() => {
        if (this.loginComponent) {
          this.loginComponent.closeLogin();
        }
      });
      return;
    }

    if (code === "107") {
      this.newLoginAlertsService.showAlertRecoverPassword().then((accepted) => {
        if (accepted) {
          if (this.loginComponent) {
            this.loginComponent.gotoStep(Steps.GetPassword);
          }
        } else {
          if (isTouchId) {
            this.nativeService.reOpenLogin();
          }
        }
      });
      return;
    }

    if (code?.indexOf("ExitQuick") >= 0) {
      this.newLoginAlertsService.showAlertExitQuick(code);
      return;
    }

    if (type && this.isInErrorCodes(type.toString())) {
      this.newLoginAlertsService.showAlertUserService(type.toString(), () => {
        if (isTouchId) {
          this.nativeService.reOpenLogin();
        }
      });
      return;
    }

    if (isTouchId) {
      this.nativeService.reOpenLogin();
    }
    this.newLoginAlertsService.showAlertDefaultLogin().then((accepted) => {
      if (accepted) {
        if (this.loginComponent) {
          this.loginComponent.gotoStep(Steps.GetPassword);
        }
      }
    });
  }

  geolocationError(errorType: LoginErrors, handleError?: VoidFunction) {
    if (errorType === LoginErrors.LoginFailureGeolocation) {
      this.utils.alertMensozaGeolocationError();
      this.geolocationService.geoFail();
      return;
    }

    if (errorType === LoginErrors.LoginFailureGeolocationServiceNotAvailable) {
      this.geolocationService.isInCabaResponse({
        data: { code: 4 },
      });
      return;
    }

    if (errorType === LoginErrors.LoginFailureGeolocationRequiredCoordinates) {
      this.isGeolocating.set(true);
      this.geolocationService
        .geolocationAlert(
          "important",
          this.globalVars.licenseType === LicenseTypes.ArgentinaMendoza
            ? "geolocation_required_mza"
            : "geolocation_required",
          true,
          true,
        )
        .then((coords) => {
          this.isGeolocating.set(false);
          if (coords) {
            this.coords = coords;
            handleError();
          } else {
            this.geolocationService.geoFail();
          }
        });
      return;
    }
  }

  canDoAutologinNative(): boolean {
    return Boolean(this.getUserName) && Boolean(this.getPasswordNative);
  }

  doLogin(userName: string, password: string, rememberMe = false) {
    this.loginData = { userName, password, rememberMe };

    this.decouplingService.login(userName, password, this.isIOSWebApp, this.coords).subscribe({
      next: async (loginResponse) => {
        if (this.globalVars.isNative) {
          try {
            await this.biometricService.setupBiometricLogin(userName, password);
          } catch (error) {
            this.logService.error(error);
          }

          if (rememberMe) {
            if (loginResponse.encryptedPassword) {
              this.setPasswordNative = loginResponse.encryptedPassword;
            }
          } else {
            this.removePassNative();
          }
          this.nativeService.setLoggedUser(
            loginResponse.playerUserName.toLowerCase(),
            password,
            loginResponse.bgtUserId,
          );
        }
        this.loginData = undefined;
        this.setUserName = loginResponse.playerUserName.toLowerCase();
        this.finishLoginOK(loginResponse, false, false);
      },
      error: (err) => {
        this.logService.error((err as ErrorDecoupling).description);
        this.finishLoginKO(err, userName, false, () => {
          this.doLogin(userName, password, rememberMe);
        });
      },
    });
  }

  doAutoLoginNative(): Observable<boolean> {
    const q = new Promise<boolean>((resolve, reject) => {
      this.decouplingService.nativeAutologin(this.getUserName, this.getPasswordNative, this.coords).subscribe({
        next: (data) => {
          this.finishLoginOK(data, true, false);

          this.trackingService.trackEvent(["AutoLoginOk", "", this.getUserName, "AutoLoginOk", "event"]);
          resolve(true);
        },
        error: (err) => {
          const { type } = err as ErrorDecoupling;
          this.coords = null;
          this.trackingService.trackEvent(["AutoLoginKO", "", this.getUserName, "AutoLoginKO", "event"]);
          if (type === ErrorCodes.UNAUTHORIZED || type === ErrorCodes.FORBIDDEN) {
            this.removePassNative();
          }
          reject(false);
        },
      });
    });
    return from(q);
  }

  loginTouchId(userName: string, password: string) {
    this.loginData = { userName, password, rememberMe: true };
    this.decouplingService.login(userName.toLowerCase(), password, this.isIOSWebApp, this.coords).subscribe({
      next: (data) => {
        this.loginData = undefined;
        this.setPasswordNative = data.encryptedPassword;
        this.finishLoginOK(data, false, true);
      },
      error: (err) => {
        const { code, description, type } = err as ErrorDecoupling;
        this.logService.error(`${code} -  ${description}`);
        if (
          type !== LoginErrors.LoginFailureGeolocation &&
          type !== LoginErrors.LoginFailureGeolocationRequiredCoordinates &&
          type !== LoginErrors.LoginFailureGeolocationServiceNotAvailable
        ) {
          this.removePassNative();
          this.biometricService.delete();
        }
        this.finishLoginKO(err, userName, true, () => {
          this.loginTouchId(userName, password);
        });
      },

      complete: () => {
        this.canOpenLogin.set(true);
      },
    });
  }

  newCaptchaUrl() {
    this.captchaUrl.next(
      this.globalVars.URLBASE + "/ViewTicket/CaptchaImage?_=" + Math.floor(Math.random() * 99999),
    );
  }

  private setAllUserData(loginResponse: DecouplingLoginResponse) {
    this.globalVars.extraUserData.limitsExpired = loginResponse.limitsExpired;
    this.globalVars.extraUserData.acceptanceComunication = !!loginResponse.acceptanceComunication;

    this.coords = null;

    if (this.globalVars.FEATURES.csrfControlFront) {
      this.globalVars.extraUserData.csrfControl = loginResponse.csrfControl;
    }
    const { documentationVerification, firstDepositDate, registrationDate, firstDeposit } = loginResponse;
    if (this.globalVars.FEATURES.MSO_GraceTimeLogicEnabled) {
      this.setGraceTimeLogin(documentationVerification, { firstDeposit, firstDepositDate, registrationDate });
    } else {
      this.setGraceTimeLogin(documentationVerification);
    }

    if (this.globalVars.FEATURES.FiveLoginPopup) {
      this.globalVars.fiveLoginOK = Boolean(loginResponse.fiveLoginOK);
    }

    if (this.globalVars.FEATURES.MSO_TerminationCodereDecision) {
      this.globalVars.user.terminationCodereDecision = loginResponse.terminationCodereDecision;
    }

    if (this.globalVars.FEATURES.MSO_DocumentationVerificationStatus) {
      this.globalVars.extraUserData.documentationVerificationStatus =
        loginResponse["documentationVerificationStatus"];
    }

    this.globalVars.user.idBGT = loginResponse.bgtUserId;
    this.globalVars.user.username = loginResponse.playerUserName.toLowerCase();
    this.globalVars.user.balance = loginResponse.balance;
    this.globalVars.user.logged = true;
    this.globalVars.user.pendingSMSEmail = loginResponse.activeSmsEmail;
    this.globalVars.user.lastPassword = loginResponse.lastChangePassword;
    this.globalVars.sessionInitDate = new Date();
    this.globalVars.user.id = loginResponse.id;
    this.globalVars.user.email = loginResponse.email;

    this.globalVars.user.lastLoginDate = loginResponse.lastLogin;
    this.globalVars.user.firstDeposit = !!loginResponse.firstDeposit;
    this.globalVars.user.registratedDate = loginResponse.registrationDate;

    if (loginResponse.userCardNumber) {
      this.globalVars.user.codereCard.cardNumber = loginResponse.userCardNumber;
      this.globalVars.user.codereCard.active = true;
      this.globalVars.user.codereCard.region = loginResponse.userCardNumber.substring(2, 2);
    } else {
      this.globalVars.user.codereCard.active = false;
    }

    if (loginResponse.userRegion) {
      this.globalVars.user.userRegion = loginResponse.userRegion;
      this.globalVars.licenseType = loginResponse.userRegion;
    }

    switch (this.globalVars.licenseType) {
      case LicenseTypes.Nacional:
        this.globalVars.extraUserData.tycSelfie = Boolean(loginResponse["policyPrivicyVerification"]);
        this.globalVars.extraUserData.professionOrPrpMissing = loginResponse["professionOrPrpMissing"] || "";
        break;
      case LicenseTypes.Colombia:
        this.globalVars.extraUserData.pptNotVerification = !!loginResponse.isPPT;
        this.globalVars.extraUserData.isRevocationPage = !!loginResponse.canRevocate;
        break;
      case LicenseTypes.Panama:
        this.globalVars.isProhibited = loginResponse.isProhibited;
        break;
      case LicenseTypes.ArgentinaCaba:
        break;
      case LicenseTypes.ArgentinaMendoza:
        this.globalVars.extraUserData.isForbiddenInMendoza = !!loginResponse.isForbidden;
        break;
    }

    if (this.utils.isArgentina() || this.globalVars.licenseType === LicenseTypes.Panama) {
      this.globalVars.user.sessionLimitsGasto = loginResponse.casinoSpendingLimit;
      this.globalVars.user.sessionLimitsPerdida = loginResponse.casinoLossLimit;
      this.globalVars.user.sessionLimitsTiempo = loginResponse.casinoTimeLimit;
    }
  }

  private setCustomization() {
    this.userService.getCustomizations().subscribe((response) => {
      this.utils.getCuotaAccept(response.customizations);
    });
  }

  private migrationUser(loginResponse: DecouplingLoginResponse, autologin = false, showMsg = true) {
    if (this.userMigrationService.isMadridUserMigrationRequired()) {
      const msg = showMsg
        ? () => {
            this.afterLoginAdditionalChecks(loginResponse, autologin);
          }
        : undefined;
      this.userMigrationService.migrateMadridUser(msg);
    } else {
      if (showMsg) {
        this.afterLoginAdditionalChecks(loginResponse, autologin);
      } else {
        this.events.publish("user:login");
        this.nativeService.checkNotificationNative();
        this.events.publish("navbarpc:refresh");
      }
    }
  }

  private setTrackersLoginOk(loginResponse: DecouplingLoginResponse, isTouchID = false) {
    const labelID = isTouchID ? "LoginOKTouchId" : "LoginOKNoTouchId";
    this.trackingService.trackEvent([labelID, "", loginResponse.playerUserName.toLowerCase(), labelID, "event"]);

    const loginEvent: TrackingEvent = {
      eventType: EventTypes.Login,
      id: this.globalVars.user.username,
      additionalData: {
        af_content_id: this.globalVars.user.username,
        userId: this.globalVars.user.username,
        contactId: this.globalVars.user.idBGT,
      },
    };

    if (this.globalVars.isNative) {
      this.appsflyerTrackingService.trackEvent("af_login", loginEvent.additionalData);
      this.nativeService.xtremeHitEvent("Login", "Login Event");
      this.analyticsService.trackEvent(AnalyticsEvent.LOGIN, {
        [CustomDimensions.USER_ID]: this.globalVars.user.username,
      });
    }

    if (this.globalVars.licenseType === LicenseTypes.Colombia) {
      this.slotsBonusesService.taboolaTrack("Login");
    }

    if (
      this.globalVars.licenseType === LicenseTypes.Colombia ||
      this.globalVars.licenseType === LicenseTypes.Nacional ||
      this.globalVars.licenseType === LicenseTypes.ArgentinaCaba
    ) {
      this.gtmTrackers(loginResponse, loginEvent);
    } else {
      this.trackingService.track(loginEvent);
    }

    if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
      // <!-- Facebook Pixel Code -->
      this.facebookPixelCode(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js",
        null,
        null,
        null,
      );
      fbq("init", "670176583366501");
      fbq("track", "Login", { content_ids: this.globalVars.user.idBGT });
    }

    this.soliticsService.login();
  }

  private gtmTrackers(loginResponse: DecouplingLoginResponse, loginEvent: TrackingEvent) {
    this.utilsMsoService
      .setGTMTrackers()
      .then((allTrackers) => {
        const auxTrackers = {
          signupDate: loginResponse.registrationDate.toISOString(),
          lastLoginDate: loginResponse.lastLogin?.toISOString(),
          balance_amt: loginResponse.balance,
          codereIdTrackers: this.globalVars.user.codereIdTracker,
          firstDeposit: this.globalVars.user.firstDeposit,
        };
        loginEvent.additionalData = {
          ...loginEvent.additionalData,
          ...auxTrackers,
        };

        this.trackingService.track(loginEvent);
        this.GTMService.addItem(
          {
            status: TrackStatus.OK,
            userId: loginEvent.additionalData.contactId,
            signupDate: auxTrackers.signupDate,
            lastLoginDate: auxTrackers.lastLoginDate,
            balance_amt: auxTrackers.balance_amt,
            advertiser: auxTrackers.codereIdTrackers.Mexos_Advertiser || "",
            profile: auxTrackers.codereIdTrackers.Mexos_ProfileID || "",
            acqSource: auxTrackers.codereIdTrackers.Mexos_Aquisition_Source || "",
            ID2: auxTrackers.codereIdTrackers.Mexos_ID2 || "",
            ID3: auxTrackers.codereIdTrackers.Mexos_ID3 || "",
          },
          "Login",
        );

        if (this.globalVars.licenseType === LicenseTypes.Nacional) {
          this.utilsMsoService.trackCookieConsentStatus(allTrackers);
        }
      })
      .catch((err) => {
        this.logService.error("eventLoginTrakingError", err);
      });
  }

  private trackGtmKo(userName: string) {
    const GTMData = this.GTMService.getStoreData(userName);
    const GtmConfig = Object.entries(GTMData)[0];
    const GtmEvent: GTMData[] = GtmConfig ? GtmConfig[1]?.Login : null;
    const gtmEventLogin = GtmEvent ? GtmEvent[0] : null;
    const loginEventKo: TrackingEvent = {
      eventType: EventTypes.GtmKo,
      id: userName.toLowerCase(),
      additionalData: {
        signupDate: gtmEventLogin?.signupDate || "",
        lastLoginDate: gtmEventLogin?.lastLoginDate || "",
        balance_amt: gtmEventLogin?.balance_amt || "",
        contactId: gtmEventLogin?.userId || 0,
        codereIdTrackers: {
          Mexos_Advertiser: gtmEventLogin?.advertiser || "",
          Mexos_ProfileID: gtmEventLogin?.profile || "",
          Mexos_ID2: gtmEventLogin?.ID2 || "",
          Mexos_ID3: gtmEventLogin?.ID3 || "",
          Mexos_Aquisition_Source: gtmEventLogin?.acqSource || "",
        },
        firstDeposit: gtmEventLogin?.ftd || "",
        type: "Login",
      },
    };
    this.trackingService.track(loginEventKo);
  }

  private afterLoginAdditionalChecks(loginResponse: DecouplingLoginResponse, autologin = false) {
    if (loginResponse["selfExclusion"] && this.globalVars.licenseType !== LicenseTypes.Colombia) {
      this.newLoginAlertsService.showAlertExtendSelftExclusion().then((result) => {
        if (result === "accept") {
          this.newAutoexclusion();
        } else {
          this.removeAutoExclusion();
          this.msgLogin(loginResponse);
        }
      });
      return;
    }

    if (!autologin) {
      this.msgLogin(loginResponse);
      this;
      return;
    }

    setTimeout(() => {
      this.events.publish("user:login");
    }, 500);
  }

  private msgLogin(loginResponse: DecouplingLoginResponse) {
    this.newLoginAlertsService.showAlertWelcomeLoginMsg(loginResponse).then(() => {
      this.events.publish("user:login");
      this.checkPasswordLogTime();
    });
    if (this.loginComponent) {
      this.loginComponent.closeLogin();
    }
  }

  private facebookPixelCode(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }

  private trackLoginKO(userName: string, isTouch = false) {
    this.events.publish("user:refreshToken");
    if (
      this.globalVars.licenseType === LicenseTypes.Nacional ||
      this.globalVars.licenseType === LicenseTypes.Colombia ||
      this.globalVars.licenseType === LicenseTypes.ArgentinaCaba
    ) {
      this.trackGtmKo(userName);
    }
    if (isTouch) {
      this.trackingService.trackEvent(["LoginKOTouchId", "", userName.toLowerCase(), "LoginKOTouchId", "event"]);
      this.biometricService.delete();
    } else {
      this.trackingService.trackEvent(["LoginKO", "", userName.toLowerCase(), "LoginKO", "event"]);
    }
  }

  private initGeolocation() {
    if (this.globalVars.FEATURES.MSO_EnableGeolocation) {
      if (this.globalVars.FEATURES.MSO_TimerGeolocation) {
        this.geolocationService.setLoginGeolocation();
      } else {
        this.geolocationService.showGeolocation();
      }
    } else if (this.utils.isArgentina()) {
      this.geolocationService.setLoginGeolocation();
    }
  }

  private removeAutoExclusion() {
    this.userServiceMSO.removeAutoExclusion().subscribe((data) => {
      if (data.success) this.logService.log("Autoexclusion eliminada");
      else this.logService.error("Eliminacion de Autoexclusion fallida");
    });
  }

  private newAutoexclusion() {
    if (this.loginComponent) {
      this.loginComponent.closeLogin();
    }
    this.globalVars.renewSelfExclusion = true;
    this.router.navigate([`/${MSO_PATHS.BASE}/${MSO_PATHS.SelfExclusionPage}`], {
      queryParams: { animate: false },
    });
  }

  private checkPasswordLogTime() {
    try {
      const region = this.globalVars.user.userRegion;
      const FechaDePassword = this.globalVars.user.lastPassword;
      if (
        (region === LicenseTypes.Nacional ||
          region === LicenseTypes.Colombia ||
          region === LicenseTypes.ArgentinaCaba) &&
        this.cookiesService.getCookie(CookieNames.WarningCoderePassword) == null
      ) {
        let limitDays = 0;
        switch (region) {
          case LicenseTypes.Nacional:
            limitDays = 180;
            break;
          case LicenseTypes.Colombia:
            limitDays = 250;
            break;
          case LicenseTypes.ArgentinaCaba:
            limitDays = 365;
            break;
        }
        if (this.checkDates(FechaDePassword) > limitDays) {
          this.newLoginAlertsService.showAlertChangePasswordTime();
          this.cookiesService.setCookie(CookieNames.WarningCoderePassword, "secure", 180 * 24 * 60 * 60 * 1000);
        } else {
          this.cookiesService.setCookie(CookieNames.WarningCoderePassword, "secure", 180 * 24 * 60 * 60 * 1000);
        }
      }
    } catch (_) {
      this.logService.error("Fail checkPasswordLogTime");
    }
  }

  private checkDates(previusDate: Date) {
    previusDate = this.extractTimeMaintenace(previusDate);
    const actualDate = new Date();
    return (actualDate.getTime() - previusDate.getTime()) / 1000 / 3600 / 24;
  }

  private extractTimeMaintenace(time: string | Date) {
    return time instanceof Date ? time : new Date(parseInt(time.slice(6, time.length - 2)));
  }

  eventsTrackTools() {
    const component = this.globalVars.isNative ? MSO_PATHS.InformationPageMobile : MSO_PATHS.InformationPage;

    this.router.navigate([`/${MSO_PATHS.BASE}/${component}`], {
      queryParams: { type: "eventstrackingtesttool", animate: this.isMobile },
      queryParamsHandling: "merge",
    });

    this.trackingService.trackEvent([
      "AccessToEventsTrackingTestToolMobile",
      "",
      "hidelink",
      "Pagina de EventsTrackingTest",
      "event",
    ]);
  }

  canGetPassword() {
    return !this.cookiesService.getCookie(CookieNames.ForgotPassword);
  }

  getNewPassword(user: string, captcha: string) {
    return new Promise<boolean>((resolve) => {
      const request = this.globalVars.FEATURES.MSO_SendLinkResetPasswordDecoupling
        ? this.decouplingService.sendLinkResetPassword(user)
        : this.userService.forgotPassword(user, captcha);
      request.subscribe({
        next: (data) => {
          if (data?.status === "ok" || this.globalVars.FEATURES.MSO_SendLinkResetPasswordDecoupling) {
            this.newLoginAlertsService.showAlertResetPasswordOk().then(() => {
              this.cookiesService.setCookie(CookieNames.ForgotPassword, "send", 1000 * 60 * 10);
              resolve(true);
            });
          } else {
            if (data.errCode === "CaptchaFailure") {
              this.newLoginAlertsService.showAlertCaptchaKO();
            } else {
              if (data.errCode !== undefined) {
                if (isNaN(data.errCode)) {
                  this.utils.showError(data.errCode);
                } else {
                  this.errorService.searchErrorCode(data.errCode).subscribe({
                    next: (data3) => {
                      this.utils.showError(data3);
                    },
                    error: (_) => {
                      this.newLoginAlertsService.showAlertSendPassKO();
                    },
                  });
                }
              } else {
                this.newLoginAlertsService.showAlertSendPassKO();
              }
            }
            resolve(false);
          }
        },
        error: () => {
          this.newLoginAlertsService.showAlertSendPassKO();
          resolve(false);
        },
      });
    });
  }

  private argentinaRedirectLicense(redirect: number, jwt: string) {
    if (!this.globalVars.isNative && this.globalVars.licenseType !== redirect) {
      window.location.replace(`${this.getURLToRedirect(redirect)}?JWT=${jwt}`);
    }
  }

  private getURLToRedirect(license: number) {
    const urls = JSON.parse(this.globalVars.FEATURES.loginURLredirect) || [];
    return urls.find((u) => u[license])[license];
  }

  private newTermsAndConditions(userName: string, isTouch = false) {
    const response =
      this.globalVars.licenseType === LicenseTypes.Nacional
        ? this.newLoginAlertsService.showAlertNewTermsAndConditionsNacional(userName)
        : this.newLoginAlertsService.showAlertNewTermsAndConditionsOthers(userName);

    response.then((accepted) => {
      if (accepted) {
        this.reLogin(isTouch);
      }
    });

    if (isTouch) {
      this.trackingService.trackEvent([
        "LoginKObynewtermsandconditionsTouchId",
        "",
        userName.toLowerCase(),
        "LoginKObynewtermsandconditionsTouchId",
        "event",
      ]);
    } else {
      if (this.globalVars.isNative) {
        this.trackingService.trackEvent([
          "AutoLoginKObynewtermsandconditions",
          "",
          userName.toLowerCase(),
          "AutoLoginKObynewtermsandconditions",
          "event",
        ]);
      } else {
        this.trackingService.trackEvent([
          "LoginKObynewtermsandconditions",
          "",
          userName.toLowerCase(),
          "LoginKObynewtermsandconditions",
          "event",
        ]);
      }
    }
  }

  private newPrivacePoliticsAlert(userName: string, isTouch = false) {
    const username = userName.toLowerCase();
    this.newLoginAlertsService.showAlertPrivacePolitics(username).then((response) => {
      if (response) {
        this.reLogin(isTouch);
      }
    });
    if (isTouch) {
      this.trackingService.trackEvent([
        "LoginKObynewprivacepoliticsTouchId",
        "",
        username,
        "LoginKObynewprivacepoliticsTouchId",
        "event",
      ]);
    } else {
      if (this.globalVars.isNative) {
        this.trackingService.trackEvent([
          "AutoLoginKObynewprivacepolitics",
          "",
          username,
          "AutoLoginKObynewprivacepolitics",
          "event",
        ]);
      } else {
        this.trackingService.trackEvent([
          "LoginKObynewprivacepolitics",
          "",
          username,
          "LoginKObynewprivacepolitics",
          "event",
        ]);
      }
    }
  }

  private reLogin(isTouch = false) {
    if (isTouch) {
      this.events.publish("user:openLogin");
    } else {
      if (this.globalVars.isNative && this.canDoAutologinNative()) {
        this.doAutoLoginNative();
      } else {
        if (this.loginComponent) {
          this.loginComponent.doLogin();
        } else {
          const { password, rememberMe, userName } = this.loginData;
          this.doLogin(userName, password, rememberMe);
        }
      }
    }
  }

  private getErrorCodes() {
    let errorCodes: string[] = [];
    errorCodes = this.globalVars.FEATURES.loginerrorCode ? this.globalVars.FEATURES.loginerrorCode.split(";") : [];
    return errorCodes;
  }

  private isInErrorCodes(errorCode: string) {
    const errorCodes = this.getErrorCodes();
    return errorCode !== undefined && errorCodes.length > 0 && errorCodes.find((e) => e === errorCode);
  }

  private checkInitSessionDate() {
    this.newUserService.getSessionInitDate().subscribe((data) => {
      const initDate = new Date(+data.match(/Date\((\d+)\)/)[1]);
      this.globalVars.sessionInitDate = initDate;
    });
  }

  public doAutoLoginCookie(callBack?: VoidFunction) {
    return new Promise<AutologinStatus>((resolve) => {
      this.decouplingService.autoLoginCookie(this.coords).subscribe({
        next: (response) => {
          this.setAllUserData(response);
          this.checkInitSessionDate();
          this.setCustomization();
          this.getUserInfo();
          this.initGeolocation();

          if (
            this.globalVars.licenseType === LicenseTypes.Nacional ||
            this.globalVars.licenseType === LicenseTypes.Colombia
          ) {
            this.utilsMsoService
              .setGTMTrackers()
              .then()
              .catch((err) => {
                this.errorService.logService.error("eventLoginTrakingError", err);
              });
          }

          if (callBack) {
            callBack();
          } else {
            this.migrationUser(response, false, false);
          }

          this.globalVars.rootScope.subscribeToRoyalDegreeSignalR();

          resolve(AutologinStatus.LOGGED);
        },
        error: (err) => {
          const { code, type } = err as ErrorDecoupling;

          if (
            type === LoginErrors.LoginFailureGeolocation ||
            type === LoginErrors.LoginFailureGeolocationServiceNotAvailable ||
            type === LoginErrors.LoginFailureGeolocationRequiredCoordinates
          ) {
            this.geolocationError(type, () => {
              this.doAutoLoginCookie(callBack).then((reAutologin) => {
                resolve(reAutologin);
              });
            });
            if (
              type === LoginErrors.LoginFailureGeolocation ||
              type === LoginErrors.LoginFailureGeolocationServiceNotAvailable
            ) {
              resolve(AutologinStatus.SESSIONERROR);
            }
          } else {
            if (code === "sessionOut") {
              this.newLoginAlertsService.showAlertSessionOut();
              this.globalVars.rootScope.closeSlotSessions();
              resolve(AutologinStatus.SESSIONOUT);
            } else {
              if (type === LoginErrors.LoginFailureRuamDown) {
                this.newLoginAlertsService.showAlertRuamDown();
              }
              resolve(AutologinStatus.SESSIONERROR);
            }
          }
        },
      });
    });
  }

  setGraceTimeLogin(
    documentationVerification: boolean,
    data?: {
      firstDeposit: boolean;
      firstDepositDate: Date;
      registrationDate: Date;
    },
  ) {
    this.utilsMsoService.setGracetime(
      documentationVerification,
      !data
        ? null
        : {
            value: this.utils.isArgentina()
              ? GraceTimeStatus.OUT_OF_TIME_FIRST_DEPOSIT
              : data.firstDeposit
                ? GraceTimeStatus.OUT_OF_TIME_FIRST_DEPOSIT
                : GraceTimeStatus.OUT_OF_TIME_REGISTER,
            maxTime: this.utils.isArgentina()
              ? Number(this.globalVars.FEATURES.MSO_MaxGraceTimeFD)
              : data.firstDeposit
                ? Number(this.globalVars.FEATURES.MSO_MaxGraceTimeFD)
                : Number(this.globalVars.FEATURES.MSO_MaxGraceTimeReg),
            dateToChek: this.utils.isArgentina()
              ? data.firstDepositDate
              : data.firstDeposit
                ? data.firstDepositDate
                : data.registrationDate,
            maxDate: this.utils.isArgentina()
              ? null
              : new Date(data.registrationDate).setDate(
                  data.registrationDate.getDate() + Number(this.globalVars.FEATURES.MSO_MaxGraceTimeReg),
                ),
          },
    );
  }

  showAlertLoginGraceTime() {
    return new Promise<void>((resolve) => {
      const literalMsg: string = this.utils.isArgentina()
        ? "gracetimeloginAr"
        : this.dataUserMsoService.getExtraUserData()?.graceTime === GraceTimeStatus.OUT_OF_TIME_FIRST_DEPOSIT
          ? "gracetimeloginpaFD"
          : "gracetimeloginpaReg";
      this.newLoginAlertsService.showAlertGraceTime(literalMsg, () => {
        this.events.publish("cashier:open", { section: "documents", cashierEmpty: false });
        resolve();
      });
    });
  }

  private async presentWelcomeBonus() {
    this.utils.modalBonusCOOpen = true;
    this.events.publish(
      "cashier:open",
      this.cookiesService.getCookie(CookieNames.IforiumPromo) === null ||
        this.cookiesService.getCookie(CookieNames.IforiumPromo) === undefined
        ? { name: "PAYU", cashierEmpty: false }
        : { cashierEmpty: false },
    );
    const dataAlert = await this.newLoginAlertsService.showAlertWelcomeBonus();
    if (dataAlert) {
      this.events.publish("cashier:endEmpty");
    }
    this.utils.modalBonusCOOpen = false;
  }

  private checkWelcomeBonus(isAvailable?: boolean): boolean {
    if (
      this.globalVars.FEATURES.ModalBonusSportCasino &&
      !this.utils.modalBonusCOOpen &&
      !localStorage.getItem("welcomebonusopened") &&
      isAvailable &&
      (!this.globalVars.FEATURES.MSO_EnabledComplianceRegistroCO ||
        !this.globalVars.extraUserData.pptNotVerification)
    ) {
      const extraUserdata = this.dataUserMsoService.getExtraUserData();
      if (this.globalVars.FEATURES.MSO_GraceTimeLogicEnabled) {
        if (extraUserdata?.graceTime === GraceTimeStatus.ON_TIME || extraUserdata?.documentationVerification) {
          return true;
        }
      } else {
        return true;
      }
    }

    return false;
  }

  getUserInfo() {
    this.decouplingService
      .getInfoPlayer()
      .pipe(
        map((response) => ({
          userInfo: this.utils.mapUserInfo(response),
          welcomeBonus: response.welcomeBonusInfo,
          freeeBetInfo: response.freeBetBonusPopupInfo,
          freezingFunds: response.isInOrdenCongelamientoFondos,
          dniExpired: response.dniExpirationVerifyInfo,
        })),
      )
      .subscribe({
        next: async (data) => {
          const { userInfo, welcomeBonus, freeeBetInfo, freezingFunds, dniExpired } = data;
          const vipStatus = userInfo.vipEstado === Constants.VipEstado.YES ? `true,${userInfo.vipLevel}` : "false";
          this.cookiesService.setCookie(CookieNames.VipStats, vipStatus, null, true);
          const userdata: I_UserInfo = userInfo;
          if (userInfo.ispromotable === undefined || userInfo.ispromotablevip === undefined) {
            try {
              const promoresponse = await firstValueFrom(this.utilsMsoService.getIsPromotable(true));
              userdata.ispromotable = promoresponse.isPromotable;
              userdata.ispromotablevip = promoresponse.isPromotableVip;
            } catch (_) {
              /* empty */
            }
          }

          this.dataUserMsoService.dataUserInfo = userdata;
          this.cookiesService.setCookie(
            CookieNames.IsPromotable,
            Boolean(userdata.ispromotablevip).toString(),
            null,
            true,
          );

          this.cookiesService.setCookie(CookieNames.UserInfo, JSON.stringify(userdata), null, true);
          this.events.publish("userInfoDone", userdata);
          const additionalRequest: (() => Promise<unknown>)[] = [];

          if (this.globalVars.FEATURES.isOrdenCongelamientoFondos) {
            this.dataUserMsoService.setExtraUserData = {
              isInOrdenCongelamientoFondos: Boolean(freezingFunds),
            };
          }
          this.dataUserMsoService.setExtraUserData = {
            dataDNIExpired: dniExpired,
          };

          if (this.globalVars.FEATURES.targetedOptinPopup && freeeBetInfo?.popups?.length) {
            const freebetConfig = freeeBetInfo.popups[0];
            if (
              freebetConfig.isActive &&
              !freebetConfig.errors.length &&
              !JSON.parse(localStorage.getItem("targetOpInOpen"))?.open
            ) {
              if (userdata && !userdata.selfExcluded && userdata.ispromotablevip) {
                additionalRequest.push(async () => {
                  await this.newLoginAlertsService.showAlertTargetOptIn(freebetConfig);
                });
              }
            }
          }

          if (welcomeBonus && this.checkWelcomeBonus(welcomeBonus.isAvailable)) {
            additionalRequest.push(() => this.presentWelcomeBonus());
          }

          additionalRequest.push(() => this.newLoginAlertsService.showAlertBonusCasino());

          this.$loginEventDone
            .pipe(
              filter((nextV) => nextV),
              take(1),
            )
            .subscribe({
              next: () => {
                this.$loginEventDone.next(false);
                this.additionalAlertLogin(additionalRequest);
                this.afterLoginAdditionalServices();
              },
            });
        },
        error: () => {
          this.dataUserMsoService.dataUserInfo = {};
        },
      });
  }

  private checkProfesion() {
    return new Promise<void>((resolve) => {
      this.userServiceMSO.verifyProfesion().subscribe({
        next: async ({ data }) => {
          this.globalVars.extraUserData.haveProfesion = data.isEnabled;
          this.globalVars.extraUserData.daysSinceFirstCheckProf = data.daysSinceFirstCheck;
          if (!data.isEnabled) {
            await this.newLoginAlertsService.showAlertProfesions(data.daysSinceFirstCheck);
          }
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }

  private async checkDNIExpired() {
    await this.newLoginAlertsService.showAlertDNIExpired(
      this.dataUserMsoService.getExtraUserData()?.dataDNIExpired?.daysSinceFirstCheck,
    );
  }

  private checkDocumentationVerificationStatus() {
    return this.newLoginAlertsService.showAlertDocumentationVerificationStatus(() => {
      this.events.publish("documentverification:open");
    });
  }

  private checkUpdateLimits() {
    return new Promise<void>((resolve) => {
      if (this.utils.isArgentina()) {
        this.utilsMsoService.updateLimits(true).then((updated) => {
          if (updated) {
            this.newLoginAlertsService.showAlertUpdateLimitsArgentina().then(() => {
              resolve();
            });
          } else {
            resolve();
          }
        });
      } else {
        this.newLoginAlertsService
          .showAlertUpdateLimits(
            () => {
              this.utilsMsoService.updateLimits(false).then();
            },
            () => {
              this.utilsMsoService.updateLimits(true).then();
            },
          )
          .then(() => {
            resolve;
          });
      }
    });
  }

  private async checkCommunications() {
    const canUpdate = await this.newLoginAlertsService.showAlertAcceptComunications();
    let comunications = { phone: false, sms: false, mail: false, alerts: false };
    if (canUpdate) {
      comunications = { phone: true, sms: true, mail: true, alerts: true };
      const request = this.globalVars.FEATURES.MSO_DecouplingCommunications
        ? this.decouplingService.updateCommunications({
            sendEmail: comunications.mail,
            sendPhoneCall: comunications.phone,
            sendSMS: comunications.sms,
            sendFax: comunications.alerts,
          })
        : this.userServiceMSO.updateComunications(comunications);
      request
        .pipe(
          filter(
            (data) =>
              (data.success && data.status === "ok") || this.globalVars.FEATURES.MSO_DecouplingCommunications,
          ),
          switchMap((_) =>
            this.globalVars.FEATURES.MSO_DecouplingCommunications
              ? this.decouplingService.updateCommercialCommunications()
              : this.userServiceMSO.SetAcceptanceComunication(),
          ),
        )
        .subscribe({
          next: (respose) => {
            console.info(respose);
            this.globalVars.extraUserData.acceptanceComunication = false;
          },
          error: (err) => console.error(err),
        });
    }
  }

  private afterLoginAdditionalServices() {
    if (this.globalVars.FEATURES.messageServiceEnabled) {
      this.checkMessages();
    }
  }

  private async additionalAlertLogin(alerts: (() => Promise<unknown>)[] = []) {
    const additionalRequest: (() => Promise<unknown>)[] = [];

    if (this.globalVars.FEATURES.FiveLoginPopup && this.globalVars.fiveLoginOK) {
      additionalRequest.push(async () => {
        await this.newLoginAlertsService.showAlertFiveLoginOrAutoexclusionAr(true);
      });
    }

    if (
      this.globalVars.FEATURES.MSO_CheckDNIExpired &&
      this.dataUserMsoService.getExtraUserData()?.dataDNIExpired?.isDniExpired
    ) {
      additionalRequest.push(() => this.checkDNIExpired());
    }
    if (this.globalVars.extraUserData.limitsExpired) {
      additionalRequest.push(() => this.checkUpdateLimits());
    }

    if (this.globalVars.extraUserData.isRevocationPage) {
      additionalRequest.push(() => this.newLoginAlertsService.showAlertRevocationAlert());
    }
    if (this.globalVars.extraUserData.acceptanceComunication) {
      additionalRequest.push(() => this.checkCommunications());
    }

    if (this.globalVars.FEATURES.MSO_GraceTimeLogicEnabled) {
      const extraUserdata = this.dataUserMsoService.getExtraUserData();
      if (extraUserdata?.graceTime !== GraceTimeStatus.ON_TIME && !extraUserdata?.documentationVerification) {
        additionalRequest.push(() => this.showAlertLoginGraceTime());
      }
    }
    if (
      this.globalVars.extraUserData.documentationVerificationStatus &&
      this.globalVars.FEATURES.MSO_DocumentationVerificationStatus
    ) {
      additionalRequest.push(() => this.checkDocumentationVerificationStatus());
    }
    if (this.globalVars.FEATURES.MSO_CheckProfesion) {
      additionalRequest.push(() => this.checkProfesion());
    }
    if (this.globalVars.extraUserData.isForbiddenInMendoza) {
      additionalRequest.push(() => this.newLoginAlertsService.alertAlertProhibitedLoginMZA());
    }
    if (this.globalVars.FEATURES.MSO_TerminationCodereDecision && this.globalVars.user.terminationCodereDecision) {
      additionalRequest.push(() => this.newLoginAlertsService.showAlertTerminationCodereDecision());
    }

    if (
      this.globalVars.FEATURES.MSO_EnabledComplianceRegistroCO &&
      this.globalVars.extraUserData.pptNotVerification
    ) {
      additionalRequest.push(async () => {
        await this.newLoginAlertsService.showAlertPPT();
        this.events.publish("documentverification:open");
      });
    }

    additionalRequest.push(...alerts);
    if (additionalRequest.length > 0) {
      try {
        for (const alert of additionalRequest) {
          const result = await alert();
          console.log(result);
        }
        this.events.publish("badges:refresh");
      } catch (error) {
        console.log(error);
      }
    }
  }

  private checkMessages() {
    this.messageService.getMessageCount().subscribe({
      next: (data) => {
        this.messageService.updateMessageCount(data);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
